/* eslint-disable react/no-array-index-key */
import { Gallery, Item } from "react-photoswipe-gallery";
import "photoswipe/dist/photoswipe.css";
import { useState, useEffect } from "react";
import { Constants } from "constants/Constants";
import Button from "common/components/Button/Button";
import DebounceInput from "common/components/DebounceInput/DebounceInput";
import Icon from "components/Icon/Icon";
import { useTranslation } from "react-i18next";
import { downloadFile } from "common/utils/downloadFile";
import FileIcon from "./FileIcon";
import EmptyResult from "../EmptyResult/EmptyResult";

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".svg", ".webp"];

function FileItem({
  value,
  onChange,
  onRemoveFile,
  disabled = false,
  editMode,
}: any) {
  const { t } = useTranslation();
  const {
    name,
    caption,
    percentage,
    state,
    url,
    ext,
    isToRemove,
    formats,
    width: imgWidth,
    height: imgHeight,
  } = value;

  const displayName = caption || name;
  const isPending = state === "pending";
  const fileUrl = url?.startsWith("blob:")
    ? url
    : `${Constants.ROOT_URL}${url}`;
  const isImage = IMAGE_EXTENSIONS.includes(ext?.toLowerCase() ?? "");
  const thumbnailUrl = formats?.thumbnail
    ? `${Constants.ROOT_URL}${formats.thumbnail.url}`
    : fileUrl;

  const [resolvedWidth, setResolvedWidth] = useState(
    imgWidth > 0 ? imgWidth : 0
  );
  const [resolvedHeight, setResolvedHeight] = useState(
    imgHeight > 0 ? imgHeight : 0
  );

  useEffect(() => {
    if (!isImage || isPending || (resolvedWidth > 0 && resolvedHeight > 0))
      return undefined;
    const img = new window.Image();
    img.onload = () => {
      if (img.naturalWidth > 0) {
        setResolvedWidth(img.naturalWidth);
        setResolvedHeight(img.naturalHeight);
      }
    };
    img.src = fileUrl;
    return () => {
      img.onload = null;
    };
  }, [fileUrl, isImage, isPending, resolvedWidth, resolvedHeight]);

  const progressBar = isPending && (
    <div className="w-full bg-emerald-100 h-1">
      <div
        className="h-1 bg-emerald-500 transition-all"
        style={{ width: `${percentage}%` }}
      />
    </div>
  );

  const renameInput = editMode && !isPending && (
    <div className="px-2 pb-2">
      <DebounceInput
        type="text"
        className="w-full text-input-compact"
        placeholder={t("forms.editName")}
        value={caption}
        onChange={onChange}
      />
    </div>
  );

  if (isToRemove) return null;

  if (isImage && !isPending) {
    return (
      <Item
        original={fileUrl}
        thumbnail={thumbnailUrl}
        width={resolvedWidth || 1200}
        height={resolvedHeight || 800}
        caption={displayName}
      >
        {({ ref, open }) => (
          <div className="flex flex-col rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="flex items-center gap-3 p-2">
              <img
                ref={ref as React.MutableRefObject<HTMLImageElement>}
                src={thumbnailUrl}
                alt={displayName}
                onClick={open}
                className="w-8 h-8 object-cover rounded cursor-pointer shrink-0"
              />
              <span
                className="text-sm truncate flex-1 text-slate-800 cursor-pointer"
                onClick={open}
              >
                {displayName}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadFile(fileUrl, name);
                  }}
                  className="p-1 rounded hover:bg-slate-100 transition-colors"
                  title="Télécharger"
                  aria-label="Télécharger"
                >
                  <Icon name="BiDownload" className="w-4 h-4 text-slate-500" />
                </button>
                {!disabled && onRemoveFile && (
                  <Button
                    type="error-line"
                    icon="TrashIcon"
                    onClick={(e: any) => {
                      e.stopPropagation();
                      onRemoveFile(value);
                    }}
                    small
                    compact
                  />
                )}
              </div>
            </div>
            {renameInput}
          </div>
        )}
      </Item>
    );
  }

  return (
    <div className="flex flex-col rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center gap-3 p-2">
        <FileIcon ext={ext} className="w-6 h-6 shrink-0" />
        <span className="text-sm truncate flex-1 text-slate-800">
          {displayName}
        </span>
        {!isPending && (
          <div className="flex items-center gap-1 shrink-0">
            {url && (
              <button
                type="button"
                onClick={() => downloadFile(fileUrl, name)}
                className="p-1 rounded hover:bg-slate-100 transition-colors"
                title="Télécharger"
                aria-label="Télécharger"
              >
                <Icon name="BiDownload" className="w-4 h-4 text-slate-500" />
              </button>
            )}
            {!disabled && onRemoveFile && (
              <Button
                type="error-line"
                icon="TrashIcon"
                onClick={() => onRemoveFile(value)}
                small
                compact
              />
            )}
          </div>
        )}
      </div>
      {progressBar}
      {renameInput}
    </div>
  );
}

function DefaultFilesList({
  onChange,
  items,
  onRemoveFile,
  disabled = false,
  editMode = false,
}: any) {
  const { t } = useTranslation();

  return items?.length ? (
    <Gallery withDownloadButton>
      <div className="flex flex-col gap-2">
        {items.map((value: any, index: number) => (
          <FileItem
            key={value.id ?? value.hash ?? index}
            onChange={(val: string) => {
              const newItems = [...items];
              newItems[index].caption = val;
              onChange(newItems);
            }}
            value={value}
            disabled={disabled}
            onRemoveFile={onRemoveFile}
            editMode={editMode}
          />
        ))}
      </div>
    </Gallery>
  ) : (
    <EmptyResult text={t("forms.noDocument")} />
  );
}

export default DefaultFilesList;
